/**
 * Consent gate for the M! widget.
 *
 * Nothing the visitor types may reach ElevenLabs before they have agreed to it,
 * so both callers of `mintCredentials` in `useAgentController` are gated on this
 * module: the lazy text session and the voice switch.
 *
 * Stored in a cookie rather than `localStorage` so it survives a browser
 * restart. Deliberately *not* read on the server: `cookies()` in a layout
 * would opt every route out of ISR (see the caching invariants in
 * `AGENTS.md`), which is a steep price for the initial state of one footer.
 */

export const CONSENT_COOKIE = "agent_consent";
/** Six months — the visitor should not be asked again on every visit. */
export const CONSENT_TTL_SECONDS = 60 * 60 * 24 * 180;

/**
 * True when consent has been given. Takes the raw cookie string
 * (`document.cookie` in the browser) so it stays pure and testable.
 *
 * Exact match per entry, not `includes`: `not_agent_consent=true` must not count.
 */
export const hasAgentConsent = (cookie: string): boolean =>
  cookie.split(";").some((entry) => entry.trim() === `${CONSENT_COOKIE}=true`);

/**
 * The `document.cookie` assignment string. Split out from the write so the flags
 * can be asserted in the node test project, which has no `document`.
 */
export const buildConsentCookie = (secure: boolean): string =>
  `${CONSENT_COOKIE}=true; path=/; max-age=${CONSENT_TTL_SECONDS}; samesite=lax` +
  (secure ? "; secure" : "");

type ConsentListener = () => void;

let consent = false;
let initialized = false;
const listeners = new Set<ConsentListener>();

const ensureInitialized = () => {
  if (initialized || typeof document === "undefined") return;
  initialized = true;
  consent = hasAgentConsent(document.cookie);
};

/**
 * Current consent state, for `useSyncExternalStore`. Pair it with a server
 * snapshot of `false`: the cookie only exists in the browser, and the panel is
 * closed on first paint anyway, so there is nothing to flash.
 */
export const getAgentConsent = (): boolean => {
  ensureInitialized();
  return consent;
};

/**
 * A real subscription, unlike the voice flag's `subscribeToNothing`: consent is
 * granted by a click *inside* the widget, so it changes mid-session.
 */
export const subscribeToAgentConsent = (
  listener: ConsentListener
): (() => void) => {
  ensureInitialized();
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Persists consent and notifies subscribers. */
export const grantAgentConsent = (): void => {
  ensureInitialized();
  if (typeof document !== "undefined") {
    document.cookie = buildConsentCookie(window.location.protocol === "https:");
  }
  if (consent) return;
  consent = true;
  listeners.forEach((listener) => listener());
};
